import { TableFilterState } from '@/components/data-table/filters-v2/types/types';
import { FILTER_OPTION_NO_VALUE } from '@/components/data-table/filters-v2/utils/no-value-filter-option';
import { mapCustomAttributeFilters } from '@/modules/common/utils/custom-attribute-filters';
import { ProjectBillingModel, ProjectStatus } from '@/modules/projects/types/project';
import { MembershipType } from '@/modules/team/types/membership-type';
import { readOptionCityId } from '@/modules/team/utils/city-option';

import {
  REPORT_FILTER_KEY_BILLING_MODEL,
  REPORT_FILTER_KEY_CITY,
  REPORT_FILTER_KEY_CLIENT,
  REPORT_FILTER_KEY_COUNTRY,
  REPORT_FILTER_KEY_DEPARTMENT,
  REPORT_FILTER_KEY_MEMBER,
  REPORT_FILTER_KEY_MEMBER_MANAGER,
  REPORT_FILTER_KEY_MEMBERSHIP_TYPE,
  REPORT_FILTER_KEY_PROJECT,
  REPORT_FILTER_KEY_PROJECT_MANAGER,
  REPORT_FILTER_KEY_PROJECT_STATUS,
  REPORT_FILTER_KEY_ROLE,
  REPORT_FILTER_KEY_SENIORITY,
} from '../constants/report-filter-keys';
import { ReportFiltersRequest } from '../types/dto';

const toArray = (value: string | string[] | undefined): string[] =>
  Array.isArray(value) ? value : value ? [value] : [];

/** "No X" sentinel → null entry (the backend matches NULL for it). */
const toNullableIds = (value: string | string[] | undefined): Array<number | null> | undefined => {
  const ids = toArray(value).map(v => (v === FILTER_OPTION_NO_VALUE ? null : Number(v)));
  return ids.length > 0 ? ids : undefined;
};

const toIds = (value: string | string[] | undefined): number[] | undefined => {
  const ids = toArray(value).map(Number);
  return ids.length > 0 ? ids : undefined;
};

const toEnumValues = <T extends string>(value: string | string[] | undefined): T[] | undefined => {
  const values = toArray(value) as T[];
  return values.length > 0 ? values : undefined;
};

/** Maps the report filter dropdown state (URL params) to the element data `filters` body. */
export const mapReportFilters = (filters: TableFilterState): ReportFiltersRequest => {
  const countries = toArray(filters[REPORT_FILTER_KEY_COUNTRY]);
  const cities = toArray(filters[REPORT_FILTER_KEY_CITY]);
  const selectedCountries = countries.filter(c => c !== FILTER_OPTION_NO_VALUE);
  const selectedCities = cities.filter(c => c !== FILTER_OPTION_NO_VALUE).map(readOptionCityId);

  const locationFilters = selectedCountries.map(country => {
    const countryCities = selectedCities.filter(c => c.country === country).map(c => c.city);
    return countryCities.length > 0 ? { country, cities: countryCities } : { country };
  });

  return {
    memberIds: toIds(filters[REPORT_FILTER_KEY_MEMBER]),
    departmentIds: toNullableIds(filters[REPORT_FILTER_KEY_DEPARTMENT]),
    roleIds: toNullableIds(filters[REPORT_FILTER_KEY_ROLE]),
    seniorityIds: toNullableIds(filters[REPORT_FILTER_KEY_SENIORITY]),
    memberManagerIds: toNullableIds(filters[REPORT_FILTER_KEY_MEMBER_MANAGER]),
    membershipTypes: toEnumValues<MembershipType>(filters[REPORT_FILTER_KEY_MEMBERSHIP_TYPE]),
    locationFilters: locationFilters.length > 0 ? locationFilters : undefined,
    withoutCountry: countries.includes(FILTER_OPTION_NO_VALUE) || undefined,
    withoutCity: cities.includes(FILTER_OPTION_NO_VALUE) || undefined,
    customAttributeFilters: mapCustomAttributeFilters(filters),
    projectIds: toIds(filters[REPORT_FILTER_KEY_PROJECT]),
    clientIds: toNullableIds(filters[REPORT_FILTER_KEY_CLIENT]),
    projectStatuses: toEnumValues<ProjectStatus>(filters[REPORT_FILTER_KEY_PROJECT_STATUS]),
    projectManagerIds: toNullableIds(filters[REPORT_FILTER_KEY_PROJECT_MANAGER]),
    billingModels: toEnumValues<ProjectBillingModel>(filters[REPORT_FILTER_KEY_BILLING_MODEL]),
  };
};
